'use client';

import React from 'react';
import { cn } from '@/utils';
import { Button } from '@/components/Buttons/Button';
import { Icon } from '@/components/Icons/Icon';
import { PauseIcon, RestartIcon, SubtitlesIcon } from './icons';

export interface SimulationControlBarProps {
  showCaptions: boolean;
  isActive: boolean;
  onToggleCaptions: () => void;
  onToggleCamera?: () => void;
  onPause?: () => void;
  onRestart?: () => void;
  /** Exit the simulation. */
  onExit: () => void;
}

/**
 * Parked playback control bar (captions/camera/pause/restart/exit). Currently
 * rendered with `display: none` (controls not yet enabled) but preserved for
 * re-activation.
 */
export const SimulationControlBar: React.FC<SimulationControlBarProps> = ({
  showCaptions,
  isActive,
  onToggleCaptions,
  onToggleCamera,
  onPause,
  onRestart,
  onExit,
}) => (
  <div
    className="absolute bottom-6 left-1/2 -translate-x-1/2 transform"
    style={{ display: 'none' }}
  >
    <div className="flex items-center gap-6">
      {/* Captions Toggle */}
      <button
        onClick={onToggleCaptions}
        className={cn(
          'flex h-14 w-14 items-center justify-center rounded-full transition-colors',
          showCaptions
            ? 'bg-brand-primary text-white'
            : 'bg-semantic-buttons-inactive-background text-semantic-buttons-inactive-text',
        )}
      >
        <SubtitlesIcon size={32} />
      </button>

      {/* Camera Toggle */}
      <button
        onClick={onToggleCamera}
        className="flex h-14 w-14 items-center justify-center rounded-full bg-semantic-buttons-inactive-background text-semantic-buttons-inactive-text transition-colors hover:bg-neutral-gray-300"
      >
        <Icon name="VideoCamera" size={32} />
      </button>

      {/* Pause */}
      <button
        onClick={onPause}
        className="flex h-14 w-14 items-center justify-center rounded-full bg-semantic-buttons-inactive-background text-semantic-buttons-inactive-text transition-colors hover:bg-neutral-gray-300"
      >
        <PauseIcon size={32} />
      </button>

      {/* Restart */}
      <button
        onClick={onRestart}
        className="flex h-14 w-14 items-center justify-center rounded-full bg-semantic-buttons-inactive-background text-semantic-buttons-inactive-text transition-colors hover:bg-neutral-gray-300"
      >
        <RestartIcon size={32} />
      </button>

      {/* Exit Button */}
      <Button variant="primary" onClick={onExit} className="h-14 px-8 py-5" disabled={!isActive}>
        Exit
      </Button>
    </div>
  </div>
);
